import React, { useContext, useEffect, useState } from "react";
import { useDropzone } from "react-dropzone";

import { SATOSContentRender } from "@/app/application/SATOSContentRender";
import { useTime } from "@/app/application/TimeContext";
import {
	GetLanguageValueAsString,
	Translate,
} from "@/app/application/Translate";
import { TranslationContext, UserContext } from "@/app/providers";
import { ProcedureButton } from "@/features/procedures/ui/components/common/ProcedureButton";
import { formatShiftDateTime, parseShiftDateTime } from "@/features/shift/lib";
import {
	type ShiftEventCrew,
	ShiftEventResponse,
	type ShiftEventShift,
	type ShiftEventSystem,
} from "@/features/shift/model/Shift/Shift";
import { shiftLogEventDataValidation } from "@/features/shift/model/validators";
import CustomCheckbox from "@/shared/forms/CustomCheckbox";
import CustomTextAreaArray from "@/shared/forms/CustomTextareaArray";
import NativeDateTimeField from "@/shared/forms/NativeDateTimeField";
import SATOSEditorField from "@/shared/forms/SATOSEditorField";
import { EditorMenuBar } from "@/shared/forms/satos-editor/EditorMenuBar";
import { TipTapExtensions } from "@/shared/forms/satos-editor/TiptapExtensions";
import { isImageAttachment } from "@/shared/lib/attachmentPreview";
import { AlertConfirmDeleteShiftEvent } from "@/shared/notifications/AlertConfirm";
import { AlertRequest } from "@/shared/notifications/AlertRequest";
import { AlertToast } from "@/shared/notifications/AlertToast";
import { ImageAttachmentPreviewDialog } from "@/shared/ui/ImageAttachmentPreviewDialog";
import {
	sharedShiftDialogActionsSx,
	sharedShiftDialogContentSx,
	sharedShiftDialogHeaderSx,
	sharedShiftDialogPanelSx,
	sharedShiftDialogPaperSx,
} from "@/styles/sharedShiftDialogStyles";
import ArrowOutwardIcon from "@mui/icons-material/ArrowOutward";
import ArrowUpwardIcon from "@mui/icons-material/ArrowUpward";
import CloseIcon from "@mui/icons-material/Close";
import ContentCopyIcon from "@mui/icons-material/ContentCopy";
import EditIcon from "@mui/icons-material/Edit";
import InsertDriveFileOutlinedIcon from "@mui/icons-material/InsertDriveFileOutlined";
import SaveIcon from "@mui/icons-material/Save";
import {
	Alert,
	Avatar,
	Box,
	Chip,
	Dialog,
	DialogActions,
	DialogContent,
	DialogTitle,
	FormControl,
	Grid,
	IconButton,
	InputLabel,
	Link,
	List,
	ListItem,
	ListItemAvatar,
	ListItemText,
	MenuItem,
	Select,
	Snackbar,
	Stack,
	TextField,
	Tooltip,
	Typography,
	alpha,
	useMediaQuery,
	useTheme,
} from "@mui/material";
import dayjs from "dayjs";
import utc from "dayjs/plugin/utc";
import { Form, Formik } from "formik";
import { EditorProvider } from "@tiptap/react";
import rehypeRaw from "rehype-raw";
import * as Yup from "yup";

import { GroupItem } from "@/model/Application/GroupItem";

import { checkAdminAccess } from "@/service/ApplicationQueries";
import {
	FetchShiftOptionsResult,
	useFetchShiftOptions,
} from "@/service/ShiftHomeQueries";
import {
	useMutateCreateShiftEventComment,
	useMutateDeleteShiftEvent,
	useMutateUploadShiftCommentImage,
	useMutateUpdateShiftEvent,
	useMutateUpdateShiftEventStatus,
} from "@/service/ShiftMutations";
import { Language } from "@/model/Language/Language";

dayjs.extend(utc);

const MAX_FILE_SIZE_BYTES = 5 * 1024 * 1024;

const ATTACHMENT_ACCEPT = {
	"application/pdf": [".pdf"],
	"application/msword": [".doc"],
	"application/vnd.openxmlformats-officedocument.wordprocessingml.document": [
		".docx",
	],
	"image/jpeg": [".jpg", ".jpeg"],
	"image/png": [".png"],
};

const toBooleanFlag = (value: unknown): boolean =>
	value === true || value === 1 || value === "1";

interface ShiftEventProps {
	page?: string;
	open: boolean;
	onClose: () => void;
	shiftEventData: ShiftEventData | null | undefined;
	selectedGroup: string | number;
}

type NamedItem = {
	Id?: number;
	Name?: string;
};

type ShiftEventComment = {
	Id?: number;
	Text?: string;
	CreatedBy?: string;
	Created?: string;
	Filename?: string;
	Filepath?: string;
};

type ShiftEventData = ShiftEventResponse & {
	Id?: number;
	Date?: string;
	Description?: string;
	LoggedBy?: string;
	LoggedByUserId?: number;
	Filename?: string;
	Filepath?: string;
	IsHandoverEvent?: boolean | number;
	IsOutOfService?: boolean | number;
	Tags?: string[];
	Comments?: ShiftEventComment[];
	Systems?: ShiftEventSystem[];
	Groups?: NamedItem[];
	DayNight?: ShiftEventShift;
	ShiftCrew?: ShiftEventCrew;
	Shift?: NamedItem;
	System?: NamedItem;
	GroupId?: number;
	SystemId?: number;
	ShiftCrewText?: string;
};

type ShiftEventFormValues = {
	id: number;
	date: string;
	description: string;
	procedureLinkHref: string;
	tags: string[];
	dayNightId: number | null;
	shiftCrewId: number | null;
	systems: number[];
	groups: number[];
	systemId: number | null;
	groupId: number | null;
	filename: string;
	filepath: string;
	isHandoverEvent: boolean;
	isOutOfService: boolean;
	comments: ShiftEventComment[];
	file?: File | null;
};

type ShiftEventAttachmentDropzoneProps = {
	fileName?: string;
	onFileSelected: (picked: File) => void;
	onClear: () => void;
};

type ProcedureDescriptionParts = {
	mainDescription: string;
	procedureReference?: string;
	procedureLinkHref?: string;
	procedureLinkLabel?: string;
};

function parseProcedureDescription(content: string | undefined): ProcedureDescriptionParts {
	if (!content) {
		return { mainDescription: "" };
	}

	const procedureLinkMatch = content.match(
		/<a\s+href="(?<href>[^"]+)">(?<label>[^<]+)<\/a>\s*$/i,
	);

	if (!procedureLinkMatch?.groups?.href || !procedureLinkMatch.groups.label) {
		return { mainDescription: content };
	}

	const beforeLink = content.slice(0, procedureLinkMatch.index).trim();
	const procedureMarker = " From Procedure ";
	const markerIndex = beforeLink.lastIndexOf(procedureMarker);

	if (markerIndex === -1) {
		return { mainDescription: content };
	}

	const mainDescription = beforeLink.slice(0, markerIndex).trim();

	return {
		mainDescription,
		procedureReference: beforeLink.slice(markerIndex + 1).replace(/\s*\.$/, "").trim(),
		procedureLinkHref: procedureLinkMatch.groups.href,
		procedureLinkLabel: procedureLinkMatch.groups.label,
	};
}

function buildProcedureDescription(
	mainDescription: string,
	procedureReference?: string,
	procedureLinkHref?: string,
	procedureLinkLabel?: string,
) {
	const trimmedMainDescription = mainDescription.trim();
	if (!procedureReference) {
		return trimmedMainDescription;
	}

	const descriptionParts = [trimmedMainDescription, ` ${procedureReference}.`].filter(
		Boolean,
	);
	const descriptionWithReference = descriptionParts.join("");

	if (!procedureLinkHref?.trim()) {
		return descriptionWithReference;
	}

	const safeLinkLabel = procedureLinkLabel?.trim() || "Link";
	return `${descriptionWithReference} <a href="${procedureLinkHref.trim()}">${safeLinkLabel}</a>`;
}

function ShiftEventAttachmentDropzone({
	fileName,
	onFileSelected,
	onClear,
}: ShiftEventAttachmentDropzoneProps) {
	const translation: Language | null = useContext(TranslationContext);
	const theme = useTheme();
	const dropzone = useDropzone({
		onDrop: (acceptedFiles: File[]) => {
			const picked = acceptedFiles[0];
			if (picked) {
				onFileSelected(picked);
			}
		},
		onDropRejected: (rejections) => {
			const err = rejections[0]?.errors?.[0];
			if (err?.code === "file-too-large") {
				void AlertToast.failed("File is too large (max 5 MB)");
			} else if (err?.code === "file-invalid-type") {
				void AlertToast.failed("File type not allowed");
			}
		},
		accept: ATTACHMENT_ACCEPT,
		maxFiles: 1,
		maxSize: MAX_FILE_SIZE_BYTES,
		multiple: false,
	});

	return (
		<Box
			{...dropzone.getRootProps()}
			sx={{
				...sharedShiftDialogPanelSx(theme),
				display: "flex",
				flexDirection: "column",
				alignItems: "center",
				justifyContent: "center",
				border: "2px dashed",
				borderColor: dropzone.isDragActive ? "primary.main" : "divider",
				py: 3,
				px: 2,
				textAlign: "center",
				cursor: "pointer",
				bgcolor: dropzone.isDragActive ? "action.hover" : "background.default",
				color: "text.secondary",
				transition: "border-color 0.2s, background-color 0.2s",
				"&:hover": {
					borderColor: "primary.main",
					bgcolor: "action.hover",
				},
			}}
		>
			<input
				key={fileName ?? "empty"}
				{...dropzone.getInputProps()}
				id="shift-event-file-input"
			/>
			{fileName ? (
				<Stack
					direction="row"
					alignItems="center"
					spacing={2}
					sx={{
						p: 2,
						borderRadius: 1,
						bgcolor: "background.paper",
						width: "100%",
						maxWidth: 400,
						border: "1px solid",
						borderColor: "divider",
					}}
				>
					<InsertDriveFileOutlinedIcon
						sx={{ color: "text.secondary", fontSize: 32 }}
					/>
					<Box sx={{ flex: 1, minWidth: 0 }}>
						<Typography variant="body2" fontWeight={600} noWrap>
							{fileName}
						</Typography>
					</Box>
					<IconButton
						onClick={(event) => {
							event.stopPropagation();
							onClear();
						}}
						color="error"
						size="small"
						aria-label={GetLanguageValueAsString(
							translation,
							"Common.RemoveFile",
							"Remove file",
						)}
					>
						<CloseIcon fontSize="small" />
					</IconButton>
				</Stack>
			) : (
				<>
					<Typography variant="body2" sx={{ mb: 0.5 }}>
						<Translate String="Shift.Event.Dropzone.Drag">
							Drag a file here or click to browse
						</Translate>
					</Typography>
					<Typography variant="caption">
						<Translate String="Shift.Event.File.Help">
							PDF, DOC, JPG, PNG · max 5 MB
						</Translate>
					</Typography>
				</>
			)}
		</Box>
	);
}

export default function ShiftEvent({
	page,
	open,
	onClose,
	shiftEventData,
	selectedGroup,
}: ShiftEventProps) {
	const translation: Language | null = useContext(TranslationContext);
	const theme = useTheme();
	const isMobile = useMediaQuery(theme.breakpoints.down("sm"));
	const { timezone } = useTime();
	const user = useContext(UserContext);
	const [isEditMode, setIsEditMode] = useState(false);
	const [editedEventData, setEditedEventData] = useState<ShiftEventData>({});
	const [newCommentText, setNewCommentText] = useState("");
	const [commentEditorKey, setCommentEditorKey] = useState(0);
	const [file, setFile] = useState<File | undefined>();
	const [fileName, setFileName] = useState<string | undefined>();
	const [filepath, setFilePath] = useState<string | undefined>();
	const [copied, setCopied] = useState(false);
	const [copySnackbarOpen, setCopySnackbarOpen] = useState(false);
	const [statusBaseline, setStatusBaseline] = useState({
		isHandoverEvent: false,
		isOutOfService: false,
	});
	const [imageAttachmentPreview, setImageAttachmentPreview] = useState<{
		url: string;
		fileName: string;
	} | null>(null);
	const shiftEventId =
		typeof shiftEventData?.Id === "number" ? shiftEventData.Id : undefined;
	// permissions
	const isAdmin = Boolean(user && checkAdminAccess(user));
	const isOwner = Boolean(
		user && shiftEventData && shiftEventData.LoggedByUserId === user.Id,
	);
	const canEdit = isAdmin || isOwner;

	const { updateShiftEvent, isUpdating } = useMutateUpdateShiftEvent(page);
	const { updateShiftEventStatus, isUpdating: isUpdatingStatus } =
		useMutateUpdateShiftEventStatus();
	const { deleteShiftEvent, isDeleting } = useMutateDeleteShiftEvent(page);
	const { createShiftEventComment, isSubmitting: isCommentSubmitting } =
		useMutateCreateShiftEventComment(page);
	const { uploadShiftCommentImage } = useMutateUploadShiftCommentImage();

	const { data: shiftOptions } =
		useFetchShiftOptions() as FetchShiftOptionsResult;

	// helpers:

	const toIdArray = (raw: unknown): number[] => {
		const arr = Array.isArray(raw) ? raw : [];
		const ids = arr.map((x) => Number(x)).filter((n) => !Number.isNaN(n));
		return Array.from(new Set(ids));
	};

	const toNullableNumber = (v: unknown): number | null => {
		if (v === "" || v === null || v === undefined) return null;
		const n = Number(v);
		return Number.isNaN(n) ? null : n;
	};

	const toIdsFromNamedItems = (items: NamedItem[] | undefined): number[] => {
		if (!Array.isArray(items)) return [];
		return items
			.map((item) => item.Id)
			.filter(
				(id): id is number => typeof id === "number" && Number.isFinite(id),
			);
	};

	const getInitialValues = (): ShiftEventFormValues => {
		const event = shiftEventData ?? {};
		const parsedDescription = parseProcedureDescription(event.Description ?? "");
		const systemsFromArray = toIdsFromNamedItems(event.Systems);
		const groupsFromArray = toIdsFromNamedItems(event.Groups);
		const systems =
			systemsFromArray.length > 0
				? systemsFromArray
				: typeof event.System?.Id === "number"
					? [event.System.Id]
					: [];
		const groups =
			groupsFromArray.length > 0
				? groupsFromArray
				: typeof event.GroupId === "number"
					? [event.GroupId]
					: [];

		const selectedGroupId =
			selectedGroup && selectedGroup !== "all" ? Number(selectedGroup) : null;
		const groupIdFromSelected =
			typeof selectedGroupId === "number" && Number.isFinite(selectedGroupId)
				? selectedGroupId
				: null;

		return {
			id: typeof event.Id === "number" ? event.Id : 0,
			date: event.Date
				? parseShiftDateTime(event.Date, timezone)?.format() ?? dayjs().format()
				: dayjs().format(),
			description: parsedDescription.mainDescription,
			procedureLinkHref: parsedDescription.procedureLinkHref ?? "",
			tags: Array.isArray(event.Tags) ? event.Tags : [],
			dayNightId:
				typeof event.DayNight?.Id === "number"
					? event.DayNight.Id
					: typeof event.Shift?.Id === "number"
						? event.Shift.Id
						: null,
			shiftCrewId:
				typeof event.ShiftCrew?.Id === "number" ? event.ShiftCrew.Id : null,
			systems,
			groups,
			systemId: systems[0] ?? null,
			groupId: groupIdFromSelected ?? groups[0] ?? null,
			filename: event.Filename ?? "",
			filepath: event.Filepath ?? "",
			isHandoverEvent: toBooleanFlag(event.IsHandoverEvent),
			isOutOfService: toBooleanFlag(event.IsOutOfService),
			comments: Array.isArray(event.Comments) ? event.Comments : [],
			file: null,
		};
	};

	const normalizePayload = (values: ShiftEventFormValues) => {
		const systems = toIdArray(values.systems);
		const groups = toIdArray(values.groups);
		return {
			...values,
			date: dayjs(values.date).utc().format(),
			isHandoverEvent: values.isHandoverEvent ? 1 : 0,
			isOutOfService: values.isOutOfService ? 1 : 0,
			systems,
			groups,
			systemId: values.systemId ?? systems[0] ?? null,
			groupId: values.groupId ?? groups[0] ?? null,
			dayNightId: values.dayNightId ?? null,
			shiftCrewId: values.shiftCrewId ?? null,
		};
	};

	const validationSchema = shiftLogEventDataValidation.concat(
		Yup.object({
			description: Yup.string().trim().required("Description is required"),
			procedureLinkHref: Yup.string()
				.trim()
				.test(
					"is-valid-procedure-link",
					"Procedure link must be a valid URL or relative path",
					(value) => {
						if (!value) return true;
						return /^(https?:\/\/|\/)/i.test(value);
					},
				),
			dayNightId: Yup.number().nullable(),
			shiftCrewId: Yup.number().nullable(),
			systemId: Yup.number().required("System is required"),
			groupId: Yup.number().required("Group is required"),
		}),
	);
	const quickToggleValidationSchema = Yup.object({
		isHandoverEvent: Yup.boolean(),
		isOutOfService: Yup.boolean(),
	});

	const setMultiAndPrimary = (
		setFieldValue: (
			field: keyof ShiftEventFormValues,
			value: unknown,
			shouldValidate?: boolean,
		) => Promise<unknown>,
		arrayField: "systems" | "groups",
		primaryField: "systemId" | "groupId",
		rawValue: unknown,
	) => {
		const ids = toIdArray(rawValue);
		void setFieldValue(arrayField, ids);
		void setFieldValue(primaryField, ids[0] ?? null); // keeps form valid
	};

	const stripHtmlTags = (val: unknown) => {
		const text =
			typeof val === "string"
				? val
				: typeof val === "number" ||
					  typeof val === "boolean" ||
					  typeof val === "bigint"
					? String(val)
					: "";
		return text
			.replace(/<[^>]*>/g, " ")
			.replace(/\s+/g, " ")
			.trim();
	};

	const hasCommentContent = (value: string) =>
		Boolean(stripHtmlTags(value)) || /<img\b/i.test(value);

	const normalizeCreatedComment = (
		rawComment: unknown,
		fallbackText: string,
	): ShiftEventComment => {
		const comment =
			rawComment && typeof rawComment === "object"
				? (rawComment as Record<string, unknown>)
				: {};

		return {
			Id:
				typeof comment.Id === "number"
					? comment.Id
					: typeof comment.id === "number"
						? comment.id
						: undefined,
			Created:
				typeof comment.Created === "string"
					? comment.Created
					: typeof comment.created === "string"
						? comment.created
						: new Date().toISOString(),
			CreatedBy:
				typeof comment.CreatedBy === "string"
					? comment.CreatedBy
					: typeof comment.createdBy === "string"
						? comment.createdBy
						: user?.Username ?? user?.Name ?? "You",
			Text:
				typeof comment.Text === "string"
					? comment.Text
					: typeof comment.text === "string"
						? comment.text
						: fallbackText,
			Filename:
				typeof comment.Filename === "string"
					? comment.Filename
					: typeof comment.filename === "string"
						? comment.filename
						: "",
			Filepath:
				typeof comment.Filepath === "string"
					? comment.Filepath
					: typeof comment.filepath === "string"
						? comment.filepath
						: "",
		};
	};

	// Toggle edit mode
	const handleEditToggle = () => {
		setIsEditMode((v) => !v);
		// no extra logic needed here, Formik will reinitialize with current shiftEventData
	};
	const handleClose = () => {
		setIsEditMode(false);
		onClose();
	};

	useEffect(() => {
		if (!open) return;

		setIsEditMode(false);
		setNewCommentText("");
		setCommentEditorKey((current) => current + 1);
		setEditedEventData(shiftEventData ? { ...shiftEventData } : {});
		setStatusBaseline({
			isHandoverEvent: toBooleanFlag(shiftEventData?.IsHandoverEvent),
			isOutOfService: toBooleanFlag(shiftEventData?.IsOutOfService),
		});

		// init attachment state
		setFile(undefined);
		setFileName(shiftEventData?.Filename ?? undefined);
		setFilePath(shiftEventData?.Filepath ?? undefined);
	}, [open, shiftEventId]);
	// ---------- UPDATED: send plain object payload (no FormData) ----------

	const visibleComments: ShiftEventComment[] =
		Array.isArray(editedEventData?.Comments)
				? editedEventData.Comments
			: Array.isArray(shiftEventData?.Comments)
				? shiftEventData.Comments
				: [];
	// --------------------------------------------------------------------

	// Comments
	const handleCommentSubmit = async () => {
		if (!hasCommentContent(newCommentText)) return;
		if (typeof shiftEventData?.Id !== "number") return;
		try {
			const request = {
				text: newCommentText,
				filename: "",
				filepath: "",
				shiftEventId: shiftEventData.Id,
			};

			const rawCreatedComment: unknown = await createShiftEventComment(request);
			const createdComment = normalizeCreatedComment(
				rawCreatedComment,
				newCommentText,
			);

			// Local optimistic update
			setEditedEventData(
				(prev) =>
					({
						...prev,
						Comments: [
							...(Array.isArray(prev?.Comments)
								? prev.Comments
								: Array.isArray(shiftEventData?.Comments)
									? shiftEventData.Comments
									: []),
							createdComment,
						],
					}) as ShiftEventData,
			);
			setNewCommentText("");
			setCommentEditorKey((current) => current + 1);
			void AlertToast.success("Comment added successfully!");
		} catch (error) {
			console.error("Error adding comment:", error);
			void AlertToast.failed("Failed to add comment.");
		}
	};

	// Avatar helpers
	function stringToColor(string: string) {
		let hash = 0;
		for (let i = 0; i < (string || "").length; i += 1) {
			hash = string.charCodeAt(i) + ((hash << 5) - hash);
		}
		let color = "#";
		for (let i = 0; i < 3; i += 1) {
			const value = (hash >> (i * 8)) & 0xff;
			color += `00${value.toString(16)}`.slice(-2);
		}
		return color;
	}
	function stringAvatar(name: string) {
		if (!name) return { sx: { bgcolor: "#ccc" }, children: "?" };
		const parts = name.split(" ");
		const initials =
			parts.length >= 2 ? `${parts[0][0]}${parts[1][0]}` : parts[0][0];
		return { sx: { bgcolor: stringToColor(name) }, children: initials };
	}

	const handleDelete = async (shiftEventId: number) => {
		try {
			await deleteShiftEvent(shiftEventId);
			void AlertToast.success("Group deleted successfully.");
			handleClose();
		} catch (error) {
			void AlertToast.failed("Failed to delete group.");
			console.error("Error deleting shift event:", error);
		}
	};

	const description = shiftEventData?.Description;
	const parsedDescription = parseProcedureDescription(description);

	// put near your other helpers (inside ShiftEvent component)
	const toPlain = stripHtmlTags;
	const handleUploadShiftCommentImage = (imageFile: File) =>
		uploadShiftCommentImage({
			file: imageFile,
			companyId: user?.CompanyId,
		});
	const joinNames = (arr: Array<NamedItem | string | number> | undefined) =>
		(Array.isArray(arr) ? arr : [])
			.map((x) => (typeof x === "object" && x !== null ? (x.Name ?? x.Id) : x))
			.filter((value): value is string | number => Boolean(value))
			.map(String)
			.join(", ");

	const getShiftTypeName = () =>
		shiftEventData?.DayNight?.Name || shiftEventData?.Shift?.Name || "N/A";

	const getCrewName = () =>
		shiftEventData?.ShiftCrew?.Name || shiftEventData?.ShiftCrewText || "N/A";

	const getGroupsText = () => {
		// prefer Groups array, else resolve GroupId from options
		const fromArray = joinNames(shiftEventData?.Groups);
		if (fromArray) return fromArray;

		const g = shiftOptions?.Groups?.find(
			(x) => x.Id === shiftEventData?.GroupId,
		);
		return (
			g?.Name ??
			(shiftEventData?.GroupId ? String(shiftEventData.GroupId) : "N/A")
		);
	};

	const getSystemsText = () => {
		const fromArray = joinNames(shiftEventData?.Systems);
		if (fromArray) return fromArray;

		const s = shiftOptions?.Systems?.find(
			(x) => x.Id === shiftEventData?.SystemId,
		);
		return (
			s?.Name ??
			(shiftEventData?.SystemId ? String(shiftEventData.SystemId) : "N/A")
		);
	};

	const getTagsText = () =>
		Array.isArray(shiftEventData?.Tags) && shiftEventData.Tags.length
			? shiftEventData.Tags.join(", ")
			: "—";

	const getFlagsText = () => {
		const flags: string[] = [];
		if (toBooleanFlag(shiftEventData?.IsOutOfService))
			flags.push("Out of service");
		if (toBooleanFlag(shiftEventData?.IsHandoverEvent)) flags.push("Handover");
		return flags.length ? flags.join(", ") : "—";
	};

	const getAttachmentText = () => {
		if (!shiftEventData?.Filename && !shiftEventData?.Filepath) return "—";
		const name = shiftEventData?.Filename ?? "Attachment";
		const path = shiftEventData?.Filepath ?? "";
		return path ? `${name} (${path})` : name;
	};

	const getCommentsText = () => {
		const comments = visibleComments;
		if (!comments.length) return "—";

		// newest last or first — choose what you prefer; below is newest first
		return comments
			.slice()
			.reverse()
			.map((c) => {
				const when = c?.Created
					? formatShiftDateTime(c.Created, timezone)
					: "";
				const who = c?.CreatedBy ? ` — ${c.CreatedBy}` : "";
				const txt = toPlain(c?.Text);
				return `• ${txt}${who}${when ? ` (${when})` : ""}`;
			})
			.join("\n");
	};

	const renderDescriptionContent = (content: string | undefined) => {
		if (!content) {
			return "N/A";
		}
		const descriptionParts = parseProcedureDescription(content);
		if (
			!descriptionParts.procedureReference ||
			!descriptionParts.procedureLinkHref ||
			!descriptionParts.procedureLinkLabel
		) {
			return (
				<SATOSContentRender
					isNotProcedureRender={true}
					rehypePlugins={[rehypeRaw]}
					content={content}
				/>
			);
		}

		return (
			<Stack spacing={1}>
				<SATOSContentRender
					isNotProcedureRender={true}
					rehypePlugins={[rehypeRaw]}
					content={descriptionParts.mainDescription || "N/A"}
				/>
				<Box
					sx={{
						borderLeft: "2px solid",
						borderColor: alpha(theme.palette.primary.main, 0.35),
						pl: 1.5,
						color: "text.secondary",
					}}
				>
					<Typography variant="body2" sx={{ fontStyle: "italic", mb: 0.5 }}>
						{descriptionParts.procedureReference}
					</Typography>
					<Link
						href={descriptionParts.procedureLinkHref}
						underline="hover"
						sx={{ fontWeight: 600 }}
					>
						{descriptionParts.procedureLinkLabel}
					</Link>
				</Box>
			</Stack>
		);
	};

	const buildCopyAllText = () => {
		const dateText = shiftEventData?.Date
			? formatShiftDateTime(shiftEventData.Date, timezone)
			: "—";

		const descriptionText = toPlain(shiftEventData?.Description) || "—";

		return [
			`Shift Event #${shiftEventData?.Id ?? "—"}`,
			`Date: ${dateText}`,
			`Logged by: ${shiftEventData?.LoggedBy ?? "—"}`,
			`Shift rotation: ${getShiftTypeName()}`,
			`Crew: ${getCrewName()}`,
			`Groups: ${getGroupsText()}`,
			`Systems: ${getSystemsText()}`,
			`Tags: ${getTagsText()}`,
			`Flags: ${getFlagsText()}`,
			`Attachment: ${getAttachmentText()}`,
			"",
			"Description:",
			descriptionText,
			"",
			"Comments:",
			getCommentsText(),
		].join("\n");
	};
	const copyAllDetails = async () => {
		try {
			const text = buildCopyAllText();
			await navigator.clipboard.writeText(text);

			setCopied(true);
			setCopySnackbarOpen(true);

			// auto-reset like your PageError
			setTimeout(() => {
				setCopied(false);
				setCopySnackbarOpen(false);
			}, 2500);
		} catch (e) {
			console.error(e);
			// if you want, you can also show an error snackbar similarly
			void AlertToast.failed("Could not copy shift event details.");
		}
	};

	return (
		<>
			<Dialog
				open={open}
				onClose={handleClose}
				maxWidth="sm"
				fullWidth
				fullScreen={isMobile}
				PaperProps={{ sx: sharedShiftDialogPaperSx(theme, isMobile) }}
			>
				<Snackbar
					open={copySnackbarOpen}
					autoHideDuration={2500}
					onClose={() => setCopySnackbarOpen(false)}
					anchorOrigin={{ vertical: "bottom", horizontal: "center" }}
				>
					<Alert
						severity="success"
						onClose={() => setCopySnackbarOpen(false)}
						sx={{ width: "100%" }}
					>
						<Translate String="Auto.Features.Shift.Ui.Components.ShiftEvent.L712C7.CopiedToClipboard">Copied to clipboard.</Translate>
					</Alert>
				</Snackbar>
				<Formik<ShiftEventFormValues>
					initialValues={getInitialValues()}
					validationSchema={
						isEditMode ? validationSchema : quickToggleValidationSchema
					}
					onSubmit={async (values) => {
						try {
							if (!isEditMode) {
								if (typeof shiftEventId !== "number") {
									AlertRequest.failed("Failed to update shift event status.");
									return;
								}

								await updateShiftEventStatus({
									shiftId: shiftEventId,
									isHandoverEvent: values.isHandoverEvent,
									isOutOfService: values.isOutOfService,
								});

								setStatusBaseline({
									isHandoverEvent: values.isHandoverEvent,
									isOutOfService: values.isOutOfService,
								});
								setEditedEventData(
									(prev) =>
										({
											...prev,
											IsHandoverEvent: values.isHandoverEvent,
											IsOutOfService: values.isOutOfService,
										}) as ShiftEventData,
								);
								void AlertToast.success();
								return;
							}

							if (!canEdit) {
								AlertRequest.failed(
									"You do not have permission to edit this shift event.",
								);
								return;
							}

							const normalized = normalizePayload(values);
							normalized.description = buildProcedureDescription(
								values.description,
								parsedDescription.procedureReference,
								values.procedureLinkHref,
								parsedDescription.procedureLinkLabel,
							);

							// attachments
							if (!file) {
								normalized.filename = fileName ?? values.filename ?? "";
								normalized.filepath = filepath ?? values.filepath ?? "";
								normalized.file = undefined;
							} else {
								normalized.file = file;
								normalized.filename = fileName ?? file.name;
								normalized.filepath = "";
							}

							if (hasCommentContent(newCommentText)) {
								await handleCommentSubmit();
							}

							await updateShiftEvent({
								shiftLogData: normalized,
								shiftEventId: normalized.id,
							});

							setStatusBaseline({
								isHandoverEvent: values.isHandoverEvent,
								isOutOfService: values.isOutOfService,
							});
							void AlertToast.success();
							setIsEditMode(false);
						} catch (err) {
							const message =
								err instanceof Error ? err.message : "Unknown error";
							AlertRequest.failed(`Failed to update shift event: ${message}`);
						}
					}}
					enableReinitialize={!isEditMode}
				>
					{({
						values,
						errors,
						touched,
						setFieldValue,
						isValid,
						isSubmitting: isFormSubmitting,
						submitForm,
					}) => {
						const isSavePending = isFormSubmitting || isUpdating;
						const isStatusSavePending = isFormSubmitting || isUpdatingStatus;
						const isAnyMutationActive =
							isSavePending ||
							isStatusSavePending ||
							isDeleting ||
							isCommentSubmitting;
						const statusChanged =
							Boolean(values.isOutOfService) !== statusBaseline.isOutOfService ||
							Boolean(values.isHandoverEvent) !==
								statusBaseline.isHandoverEvent;
						const canSaveStatus =
							!isEditMode && statusChanged && typeof shiftEventId === "number";

						return (
							<Form
								style={{
									display: "flex",
									flexDirection: "column",
									flex: 1,
									minHeight: 0,
								}}
							>
								{/* -------- Header band -------- */}
								<DialogTitle
									sx={{ p: 0, position: "relative", pb: 2, flexShrink: 0 }}
								>
									<Box
										sx={{
											...sharedShiftDialogHeaderSx(theme),
											pr: 7,
											pl: 2,
											py: 1.5,
											display: "flex",
											alignItems: "center",
											gap: 1.25,
										}}
									>
										<Box sx={{ minWidth: 0 }}>
											<Typography
												variant="h6"
												fontWeight={800}
												sx={{ lineHeight: 1.2 }}
												noWrap
											>
												<Translate String="Shift.Event.Shift.Event">
													Shift Event
												</Translate>
												&nbsp;#
												{shiftEventData?.Id}
											</Typography>

											{/* Meta row */}
											<Box
												sx={{
													mt: 0.5,
													display: "flex",
													alignItems: "center",
													flexWrap: "wrap",
													gap: 1,
													color: "text.secondary",
												}}
											>
												{/* Logged by */}
												<Tooltip title={shiftEventData?.LoggedBy || ""}>
													<Box
														sx={{
															display: "inline-flex",
															alignItems: "center",
															gap: 0.75,
														}}
													>
														<Avatar
															sx={{ width: 22, height: 22, fontSize: 12 }}
														>
															{(shiftEventData?.LoggedBy || "U")
																.split(" ")
																.map((n: string) => n[0])
																.join("")
																.slice(0, 2)
																.toUpperCase()}
														</Avatar>
														<Typography variant="caption">
															<Translate String="Shift.Event.Logged.By">
																Logged by
															</Translate>
															&nbsp;
															{shiftEventData?.LoggedBy || "—"}
														</Typography>
													</Box>
												</Tooltip>

												<Typography variant="caption" sx={{ px: 0.5 }}>
													•
												</Typography>

												{/* Date */}
												{shiftEventData?.Date ? (
													<Typography variant="caption">
														{formatShiftDateTime(shiftEventData.Date, timezone)}
													</Typography>
												) : (
													<Typography variant="caption">—</Typography>
												)}

												{/* Status chips */}
												{toBooleanFlag(shiftEventData?.IsOutOfService) && (
													<Chip
														size="small"
														color="warning"
														variant="outlined"
														label={
															<Translate String="Shift.Event.OutOfService">
																Out of service
															</Translate>
														}
														sx={{ ml: 0.5 }}
													/>
												)}
												{toBooleanFlag(shiftEventData?.IsHandoverEvent) && (
													<Chip
														size="small"
														color="info"
														variant="outlined"
														label={
															<Translate String="Shift.Event.Handover">
																Handover
															</Translate>
														}
													/>
												)}
											</Box>
										</Box>
										<Box sx={{ ml: "auto" }} />
										<Tooltip title={copied ? "Copied!" : "Copy all details"}>
										<IconButton
											onClick={() => {
												void copyAllDetails();
											}}
											size="small"
											sx={{
												border: "1px solid",
												borderColor: "divider",
												borderRadius: 2,
												bgcolor: "background.paper",
												color: copied ? "success.main" : "text.secondary",
												"&:hover": {
													color: "text.primary",
													backgroundColor: "action.hover",
												},
											}}
										>
											<ContentCopyIcon fontSize="small" />
										</IconButton>
										</Tooltip>

										{/* Close */}
										<IconButton
											aria-label={GetLanguageValueAsString(
												translation,
												"Common.Close",
												"Close",
											)}
											onClick={handleClose}
											size="small"
											disabled={isAnyMutationActive}
											sx={{
												border: "1px solid",
												borderColor: "divider",
												borderRadius: 2,
												bgcolor: "background.paper",
												color: "text.secondary",
												"&:hover": {
													color: "text.primary",
													backgroundColor: "action.hover",
												},
											}}
										>
											<CloseIcon fontSize="small" />
										</IconButton>
									</Box>
								</DialogTitle>

								{/* -------- Body -------- */}
								<DialogContent
									sx={{
										...sharedShiftDialogContentSx(theme),
										py: 2,
										flex: 1,
										minHeight: 0,
										overflowY: "auto",
										WebkitOverflowScrolling: "touch",
									}}
								>
									<Grid container spacing={2}>
										{/* Date */}
										<Grid item xs={12} sm={12}>
											{isEditMode ? (
												<>
													<NativeDateTimeField
														label={
															<Translate String="Shift.Event.Date">
																Date
															</Translate>
														}
														name="date"
														id="date"
													/>
													<br />
												</>
											) : (
												renderInfoItem(
													<Translate String="Shift.Event.Date">Date</Translate>,
													formatShiftDateTime(shiftEventData?.Date, timezone),
												)
											)}
										</Grid>

										{/* Day/Night (Shift type) */}
										<Grid item xs={12} sm={6}>
											{isEditMode ? (
												<>
													<FormControl
														fullWidth
														error={Boolean(
															touched.dayNightId && errors.dayNightId,
														)}
													>
															<InputLabel id="shiftRotation-edit-label">
																<Translate String="Shift.Event.ShiftRotation">
																	Shift Rotation
																</Translate>
															</InputLabel>
														<Select
															label={
																<Translate String="Shift.Event.ShiftRotation">
																	Shift Rotation
																</Translate>
															}
															name="dayNightId"
															value={values.dayNightId ?? ""}
															onChange={(e) => {
																void setFieldValue(
																	"dayNightId",
																	toNullableNumber(e.target.value),
																);
															}}
														>
																<MenuItem value="">
																	<em>
																		<Translate String="Common.None">None</Translate>
																	</em>
																</MenuItem>
															{shiftOptions?.ShiftTypes?.map(
																(item: GroupItem) => (
																	<MenuItem key={item.Id} value={item.Id}>
																		{item.Name}
																	</MenuItem>
																),
															)}
														</Select>
													</FormControl>
												</>
											) : (
												renderInfoItem(
													<Translate String="Shift.Event.ShiftRotation">
														Shift Rotation
													</Translate>,
													shiftEventData?.DayNight?.Name || "-",
												)
											)}
										</Grid>

										{/* Crew names */}
										<Grid item xs={12} sm={6}>
											{isEditMode ? (
												<>
													<FormControl fullWidth>
															<InputLabel id="crew-edit-label">
																<Translate String="Shift.Event.CrewName">
																	Crew Name
																</Translate>
															</InputLabel>
														<Select
															label={
																<Translate String="Shift.Event.CrewName">
																	Crew Name
																</Translate>
															}
															name="shiftCrewId"
															value={values.shiftCrewId ?? ""}
															onChange={(e) => {
																void setFieldValue(
																	"shiftCrewId",
																	toNullableNumber(e.target.value),
																);
															}}
														>
																<MenuItem value="">
																	<em>
																		<Translate String="Common.None">None</Translate>
																	</em>
																</MenuItem>
															{shiftOptions?.Crews?.map((c) => (
																<MenuItem key={c.Id} value={c.Id}>
																	{c.Name}
																</MenuItem>
															))}
														</Select>
													</FormControl>
												</>
											) : (
												renderInfoItem(
													<Translate String="Shift.Event.CrewName">
														Crew Name
													</Translate>,
													shiftEventData?.ShiftCrew?.Name || "-",
												)
											)}
										</Grid>

										{/* Systems (multiple) */}
										<Grid item xs={12} sm={6}>
											{isEditMode ? (
												<>
													<FormControl
														fullWidth
														error={Boolean(touched.systems && errors.systems)}
													>
															<InputLabel id="systems-edit-label">
																<Translate String="Shift.Event.Systems">
																	Systems
																</Translate>
															</InputLabel>
														<Select
															label={
																<Translate String="Shift.Event.Systems">
																	Systems
																</Translate>
															}
															multiple
															value={values.systems ?? []}
															onChange={(e) =>
																setMultiAndPrimary(
																	setFieldValue,
																	"systems",
																	"systemId",
																	e.target.value,
																)
															}
														>
															{(shiftOptions?.Systems || []).map((s) => (
																<MenuItem key={s.Id} value={s.Id}>
																	{s.Name}
																</MenuItem>
															))}
														</Select>

														{/* if any system errors (unlikely), show */}
														{touched.systems && errors.systems && (
															<Typography variant="caption" color="error">
																{String(errors.systems)}
															</Typography>
														)}
													</FormControl>
												</>
											) : (
												renderListInfoItem(
													<Translate String="Shift.Event.Systems">
														Systems
													</Translate>,
													shiftEventData?.Systems, // array of systems from backend
													shiftEventData?.System, // single system fallback
													"N/A",
												)
											)}
										</Grid>

										{/* Groups (multiple) */}
										<Grid item xs={12} sm={6}>
											{isEditMode ? (
												<>
													<FormControl
														fullWidth
														error={Boolean(touched.groups && errors.groups)}
													>
															<InputLabel id="groups-edit-label">
																<Translate String="Shift.Event.Groups">
																	Groups
																</Translate>
															</InputLabel>
														<Select
															label={
																<Translate String="Shift.Event.Groups">
																	Groups
																</Translate>
															}
															multiple
															value={values.groups ?? []}
															onChange={(e) =>
																setMultiAndPrimary(
																	setFieldValue,
																	"groups",
																	"groupId",
																	e.target.value,
																)
															}
														>
															{(shiftOptions?.Groups || []).map((g) => (
																<MenuItem key={g.Id} value={g.Id}>
																	{g.Name}
																</MenuItem>
															))}
														</Select>

														{touched.groups && errors.groups && (
															<Typography variant="caption" color="error">
																{String(errors.groups)}
															</Typography>
														)}
													</FormControl>
												</>
											) : (
												renderListInfoItem(
													<Translate String="Shift.Event.Groups">
														Groups
													</Translate>,
													shiftEventData?.Groups, // array of groups from backend
													// fallback: try to resolve single Group name via GroupId from options
													shiftOptions?.Groups?.find(
														(item) => item.Id === shiftEventData.GroupId,
													) ?? null,
													"N/A",
												)
											)}
										</Grid>

										{/* Tags */}
										<Grid item xs={12}>
											{isEditMode ? (
												<FormControl
													fullWidth
													error={Boolean(touched.tags && errors.tags)}
												>
													<Typography sx={{ mb: 1, fontSize: "16px" }}>
														<Translate String="Shift.Event.Tags">
															Tags
														</Translate>
													</Typography>
													<CustomTextAreaArray
														label=""
														name="tags"
														id="tags"
														fullWidth
													/>
													{touched.tags && errors.tags && (
														<Typography variant="caption" color="error">
															{Array.isArray(errors.tags)
																? errors.tags.map(String).join(", ")
																: String(errors.tags)}
														</Typography>
													)}
												</FormControl>
											) : (
												shiftEventData?.Tags?.length > 0 && (
													<Box>
														<Typography
															variant="subtitle2"
															color="text.secondary"
															component="span"
														>
															<Translate String="Shift.Event.Tags">
																Tags
															</Translate>
															:
														</Typography>
														<Box
															component="span"
															sx={{
																ml: 1,
																display: "inline-flex",
																flexWrap: "wrap",
																gap: 0.5,
															}}
														>
															{shiftEventData?.Tags.map(
																(tag: string, i: number) => (
																	<Chip
																		key={i}
																		label={tag}
																		color="success"
																		variant="outlined"
																		size="small"
																	/>
																),
															)}
														</Box>
													</Box>
												)
											)}
										</Grid>

										{/* Description */}
										<Grid item xs={12}>
											{isEditMode ? (
												<FormControl
													fullWidth
													error={Boolean(
														touched.description && errors.description,
													)}
												>
														<SATOSEditorField
															id="description"
															name="description"
															label={GetLanguageValueAsString(
																translation,
																"Common.Description",
																"Description",
															)}
														aria-required="true"
														aria-describedby="description-error-text"
														style={{ minWidth: "100%" }}
														imageUploadDisabled={true}
													/>
													{touched.description && errors.description && (
														<Typography
															id="description-error-text"
															variant="caption"
															color="error"
														>
															{String(errors.description)}
														</Typography>
													)}
												</FormControl>
											) : (
												renderInfoItem(
													<Translate String="Task.Description">
														Description
													</Translate>,
													renderDescriptionContent(description),
												)
											)}
										</Grid>
										{isEditMode && parsedDescription.procedureReference && (
											<Grid item xs={12}>
												<TextField
													fullWidth
													label="Procedure link"
													name="procedureLinkHref"
													value={values.procedureLinkHref}
													onChange={(event) => {
														void setFieldValue(
															"procedureLinkHref",
															event.target.value,
														);
													}}
													error={Boolean(
														touched.procedureLinkHref &&
															errors.procedureLinkHref,
													)}
													helperText={
														touched.procedureLinkHref &&
														errors.procedureLinkHref
															? String(errors.procedureLinkHref)
															: "Optional. Change or clear the procedure link without editing the description body."
													}
												/>
											</Grid>
										)}

										{/* Attachments */}
										{isEditMode ? (
											<Grid item xs={12}>
												<Typography
													variant="body2"
													sx={{ mb: 1, fontWeight: 600 }}
												>
													<Translate String="Shift.Event.Attachment">
														Attachment
													</Translate>
												</Typography>
												<ShiftEventAttachmentDropzone
													fileName={fileName}
													onFileSelected={(picked) => {
														setFile(picked);
														setFileName(picked.name);
														setFilePath(undefined);
														void setFieldValue("file", picked);
														void setFieldValue("filename", picked.name);
														void setFieldValue("filepath", "");
													}}
													onClear={() => {
														setFile(undefined);
														setFileName(undefined);
														setFilePath(undefined);
														void setFieldValue("file", null);
														void setFieldValue("filename", "");
														void setFieldValue("filepath", "");
													}}
												/>
											</Grid>
										) : (
											shiftEventData?.Filename && (
												<Grid item xs={12}>
													<Typography
														variant="subtitle2"
														color="text.secondary"
														component="span"
														sx={{ mr: 1 }}
													>
														<Translate String="Shift.Event.Attachment">
															Attachment
														</Translate>
													</Typography>
													<Link
														href={shiftEventData?.Filepath}
														target={
															isImageAttachment({
																Url: shiftEventData?.Filepath,
																FileName: shiftEventData?.Filename,
															})
																? undefined
																: "_blank"
														}
														rel="noopener noreferrer"
														underline="hover"
														onClick={(event) => {
															if (
																!isImageAttachment({
																	Url: shiftEventData?.Filepath,
																	FileName: shiftEventData?.Filename,
																})
															)
																return;
															event.preventDefault();
															setImageAttachmentPreview({
																url: shiftEventData?.Filepath ?? "",
																fileName:
																	shiftEventData?.Filename ??
																	"Image attachment",
															});
														}}
													>
														<Box
															component="span"
															sx={{
																display: "inline-flex",
																alignItems: "center",
																gap: 0.5,
															}}
														>
															<ArrowOutwardIcon fontSize="small" />
															{shiftEventData?.Filename}
														</Box>
													</Link>
												</Grid>
											)
										)}

										{/* Out of Service */}
										<Grid item xs={12} sm={6}>
												<Typography
													component="span"
													sx={{ mb: 1, fontSize: 16, color: "text.secondary" }}
												>
													<Translate String="Shift.Event.OutOfService">
														Out of service
													</Translate>
												</Typography>
												<Box sx={{ ml: 1 }}>
													<CustomCheckbox
														label=""
														name="isOutOfService"
														id="isOutOfService"
														type="checkbox"
														disabled={isAnyMutationActive}
													/>
												</Box>
											</Grid>

										{/* Handover Event */}
										<Grid item xs={12} sm={6}>
												<Typography
													component="span"
													sx={{ mb: 1, fontSize: 16, color: "text.secondary" }}
												>
													<Translate String="Shift.Event.HandoverEvent">
														Handover event
													</Translate>
												</Typography>
												<Box sx={{ ml: 1 }}>
													<CustomCheckbox
														label=""
														name="isHandoverEvent"
														id="isHandoverEvent"
														type="checkbox"
														disabled={isAnyMutationActive}
													/>
												</Box>
											</Grid>

										{/* Comments */}
										<Grid item xs={12}>
											<Typography variant="subtitle2" sx={{ mb: 1 }}>
												<Translate String="Shift.Event.Comments">
													Comments:
												</Translate>
											</Typography>

											{visibleComments.length > 0 ? (
													<List
														sx={{
															...sharedShiftDialogPanelSx(theme),
															maxHeight: 300,
															overflowY: "auto",
															mb: 2,
															p: 1.25,
														}}
													>
													{visibleComments
														.slice()
														.reverse()
														.map((comment, index: number) => (
															<ListItem
																key={index}
																alignItems="flex-start"
																sx={{ px: 0 }}
															>
																<ListItemAvatar>
																	<Tooltip title={comment?.CreatedBy || ""}>
																		<Avatar
																			{...stringAvatar(comment?.CreatedBy ?? "")}
																			alt={comment?.CreatedBy}
																		/>
																	</Tooltip>
																</ListItemAvatar>
																<ListItemText
																	primary={
																		<Box
																			sx={{
																				...sharedShiftDialogPanelSx(theme),
																				px: 2,
																				py: 1,
																				borderRadius: 3,
																				display: "inline-block",
																				maxWidth: "100%",
																				backgroundColor:
																					theme.palette.mode === "dark"
																						? alpha(
																							theme.palette.common.white,
																							0.03,
																						)
																						: alpha(
																							theme.palette.common.black,
																							0.01,
																						),
																			}}
																		>
																			<SATOSContentRender
																				rehypePlugins={[rehypeRaw]}
																				content={comment.Text}
																			/>
																		</Box>
																	}
																	secondary={
																		<Typography
																			variant="caption"
																			color="text.secondary"
																			sx={{ display: "block", mt: 0.5 }}
																		>
																			<Translate String="ShiftEvent.CreatedBy">
																				Created by
																			</Translate>{" "}
																			{comment.CreatedBy}{" "}
																			<Translate String="ShiftEvent.On">
																				on
																			</Translate>{" "}
																			{formatShiftDateTime(comment.Created, timezone)}
																		</Typography>
																	}
																/>
															</ListItem>
														))}
												</List>
											) : (
												<Typography
													variant="body2"
													color="text.secondary"
													sx={{ mb: 2 }}
												>
													<Translate String="ShiftEvent.NoCommentsAvailable">
														No comments available
													</Translate>
												</Typography>
											)}

											{/* New Comment */}
											<Stack spacing={1.5}>
												<Box
													sx={{
														...sharedShiftDialogPanelSx(theme),
														p: 0,
														borderRadius: 2.5,
														overflow: "hidden",
														opacity: isAnyMutationActive ? 0.72 : 1,
														pointerEvents: isAnyMutationActive ? "none" : "auto",
														"& .control-group": {
															gap: 0,
														},
														"& .button-group": {
															py: 1,
														},
														"& .tiptap": {
															margin: 0,
															minHeight: 160,
															border: "none",
															padding: "1rem",
															backgroundColor: "transparent",
														},
													}}
												>
													<EditorProvider
														key={commentEditorKey}
														extensions={TipTapExtensions}
														content={newCommentText}
														editable={
															!isCommentSubmitting && !isAnyMutationActive
														}
														slotBefore={
															<EditorMenuBar
																uploadImageRequest={handleUploadShiftCommentImage}
															/>
														}
														onUpdate={({ editor }) => {
															setNewCommentText(editor.getHTML());
														}}
														editorProps={{
															attributes: {
																id: "shift-event-comment-editor",
																"aria-label": "Shift event comment editor",
															},
														}}
													/>
												</Box>
												<Stack
													direction={{ xs: "column", sm: "row" }}
													spacing={1}
													alignItems={{ xs: "stretch", sm: "center" }}
													justifyContent="space-between"
												>
													<Typography
														variant="caption"
														color="text.secondary"
													>
														Use formatting and add images directly in your
														comment.
													</Typography>
													<Box sx={{ display: "flex", justifyContent: "flex-end" }}>
														<ProcedureButton
															intent="secondary"
															onClick={() => {
																void handleCommentSubmit();
															}}
															disabled={
																!hasCommentContent(newCommentText) ||
																isCommentSubmitting ||
																isAnyMutationActive
															}
															endIcon={
																isCommentSubmitting ? undefined : (
																	<ArrowUpwardIcon />
																)
															}
															loading={isCommentSubmitting}
															loadingText="Adding comment..."
														>
															<Translate String="ShiftEvent.Send">Send</Translate>
														</ProcedureButton>
													</Box>
												</Stack>
											</Stack>
										</Grid>
									</Grid>
								</DialogContent>

								{/* -------- Actions -------- */}
								<DialogActions
									sx={{
										...sharedShiftDialogActionsSx(theme, isMobile),
										py: 1.25,
										px: 2,
									}}
								>
									{!isEditMode && canEdit && (
										<ProcedureButton
											intent="primary"
											onClick={handleEditToggle}
											variant="outlined"
											startIcon={<EditIcon />}
											disabled={isAnyMutationActive}
											sx={{
												borderRadius: 2,
												textTransform: "none",
												fontWeight: 800,
											}}
										>
											<Translate String="Shift.Event.Edit">Edit</Translate>
										</ProcedureButton>
									)}

									{isAdmin && (
										<ProcedureButton
											intent="danger"
											loading={isDeleting}
											loadingText={
												<Translate String="ShiftEvent.Deleting">
													Deleting...
												</Translate>
											}
											onClick={() =>
												AlertConfirmDeleteShiftEvent(() => {
													if (shiftEventId != null) {
														void handleDelete(shiftEventId);
													}
												}, shiftEventId ?? 0)
											}
											disabled={isAnyMutationActive}
											variant="outlined"
											sx={{
												borderRadius: 2,
												textTransform: "none",
												fontWeight: 800,
											}}
										>
											<Translate String="Shift.Event.Delete">Delete</Translate>
										</ProcedureButton>
									)}

									{isEditMode && (
										<ProcedureButton
											intent="warning"
											startIcon={<CloseIcon />}
											onClick={handleEditToggle}
											disabled={isAnyMutationActive}
											variant="outlined"
											sx={{
												borderRadius: 2,
												textTransform: "none",
												fontWeight: 800,
											}}
										>
											<Translate String="Shift.Event.Cancel">Cancel</Translate>
										</ProcedureButton>
									)}

									<Box sx={{ flex: 1 }} />

										{((canEdit && isEditMode) || canSaveStatus) && (
											<ProcedureButton
												intent="success"
												loading={
													isEditMode ? isSavePending : isStatusSavePending
												}
												loadingText={
													<Translate String="ShiftEvent.Saving">
														Saving...
												</Translate>
											}
											startIcon={<SaveIcon />}
											type="submit"
											onClick={() => {
												void submitForm();
											}}
											disabled={
												isAnyMutationActive ||
												(isEditMode ? !isValid : !canSaveStatus)
											}
											>
												<Translate String="Common.Save">Save</Translate>
											</ProcedureButton>
										)}
								</DialogActions>
							</Form>
						);
					}}
				</Formik>
			</Dialog>
			<ImageAttachmentPreviewDialog
				preview={imageAttachmentPreview}
				onClose={() => setImageAttachmentPreview(null)}
			/>
		</>
	);
}

export function renderInfoItem(label: React.ReactNode, value: React.ReactNode) {
	return (
		<Box sx={{ mb: 1 }}>
			<Typography variant="subtitle2" color="text.secondary">
				{label}
			</Typography>
			<Typography
				variant="body1"
				sx={{ fontWeight: 500, wordBreak: "break-word" }}
			>
				{value}
			</Typography>
		</Box>
	);
}

function renderListInfoItem(
	label: React.ReactNode,
	items?: Array<{ Id?: number; Name?: string }>,
	singleItem?: { Id?: number; Name?: string } | null,
	fallback = "N/A",
) {
	const hasArray = Array.isArray(items) && items.length > 0;
	const hasSingle =
		singleItem && (singleItem.Name || singleItem.Id !== undefined);
	const safeItems = hasArray ? items : [];
	const safeSingle = hasSingle ? singleItem : null;

	const MAX_LEN = 20;

	const trimName = (value?: string | number) =>
		typeof value === "string" && value.length > MAX_LEN
			? `${value.substring(0, MAX_LEN)}...`
			: String(value ?? "");

	const content = hasArray ? (
		<Box
			sx={{
				display: "inline-flex",
				gap: 0.5,
				flexWrap: "wrap",
				alignItems: "center",
			}}
		>
			{safeItems.map((it) => (
				<Tooltip key={it.Id ?? it.Name} title={it.Name ?? String(it.Id)}>
					<Chip label={trimName(it.Name ?? it.Id)} size="small" />
				</Tooltip>
			))}
		</Box>
	) : hasSingle ? (
		trimName(safeSingle?.Name ?? safeSingle?.Id)
	) : (
		fallback
	);

	return (
		<Box sx={{ mb: 1 }}>
			<Typography variant="subtitle2" color="text.secondary">
				{label}
			</Typography>
			<Typography
				variant="body1"
				sx={{ fontWeight: 500, wordBreak: "break-word" }}
			>
				{content}
			</Typography>
		</Box>
	);
}
